// The public `/signup` page (see `features/company-registration.md`): a
// four-step wizard where a visitor signs their sport club up. Step 1 stages the
// Company Admin account and verifies the email address with an emailed
// passcode — nothing later is possible until it is confirmed. Steps 2–4 stage
// the club's name + location, description + logo, and optional photos. Finishing
// creates the company in the UNVERIFIED state (plus the admin account) and the
// platform emails "we are checking your company details"; a Super Admin's later
// review verifies the club and notifies the admin again.
//
// Renders outside `AuthProvider` (forked in `main.tsx`): the visitor has no
// session at any point — the wizard's capability is the sign-up's own
// unguessable id, which is kept in sessionStorage so an accidental reload
// resumes instead of restarting.

import { useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Tooltip from '@mui/material/Tooltip';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import {
  COMPANY_PHOTO_MAX,
  OTP_CODE_LENGTH,
  PASSWORD_MIN,
  isOtpCodeFormat,
  isValidEmail,
  localizedLocationLabel,
  locationMatchesQuery,
  sortLocationsByName,
  type CompanyRegistrationView,
  type Location,
} from '../../domain';
import {
  SignupError,
  deleteSignupPhoto,
  fetchPublicLocations,
  fetchSignup,
  finalizeSignup,
  resendSignupCode,
  saveSignupDescription,
  saveSignupDetails,
  startSignup,
  uploadSignupLogo,
  uploadSignupPhoto,
  verifySignupEmail,
} from '../../data/publicClient';
import { useTranslation } from '../../state/I18nContext';
import { BrandLogo } from '../BrandLogo';
import { LanguageSelector } from '../LanguageSelector';
import { CoverImagePicker } from '../CoverImagePicker';

/** Where the in-progress sign-up id lives, so a reload resumes the wizard. */
const STORAGE_KEY = 'tae.signupId';

/** Image MIME types accepted for club photos (matches the server allow-list). */
const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/webp'];

/** The wizard's steps, in order (indices drive the MUI Stepper). */
const STEP_KEYS = [
  'signup.steps.account',
  'signup.steps.club',
  'signup.steps.profile',
  'signup.steps.photos',
] as const;

function storedSignupId(): string | null {
  try {
    return window.sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function rememberSignupId(id: string | null): void {
  try {
    if (id === null) window.sessionStorage.removeItem(STORAGE_KEY);
    else window.sessionStorage.setItem(STORAGE_KEY, id);
  } catch {
    // Storage unavailable (private mode): the wizard still works, it just
    // cannot survive a reload.
  }
}

export function SignupWizard() {
  const { t, locale } = useTranslation();

  const [registration, setRegistration] = useState<CompanyRegistrationView | null>(null);
  const [step, setStep] = useState(0);
  // Step 1's sub-state: the account form, then the emailed-passcode prompt.
  const [phase, setPhase] = useState<'form' | 'code'>('form');
  const [restoring, setRestoring] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [done, setDone] = useState(false);

  const [adminName, setAdminName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [resent, setResent] = useState(false);

  const [locations, setLocations] = useState<Location[]>([]);
  const [clubName, setClubName] = useState('');
  const [locationId, setLocationId] = useState<number | ''>('');
  // The city popup's open state. This step is a <form>, and MUI only swallows
  // Enter when an option is highlighted — otherwise it falls through to the
  // browser's implicit submit and the wizard advances with the *previous*
  // city, which can never be changed once the club is created.
  const [locationOpen, setLocationOpen] = useState(false);
  // Listed under the names the picker displays, which collate differently from
  // the canonical English order the server sends.
  const sortedLocations = useMemo(
    () => sortLocationsByName(locations, locale),
    [locations, locale],
  );

  const [description, setDescription] = useState('');
  const [logoBlob, setLogoBlob] = useState<Blob | null>(null);

  const photoInputRef = useRef<HTMLInputElement>(null);

  // Restore an in-progress sign-up (a reloaded tab) and load the location
  // dictionary. The stored id resolves to the staged state, which decides the
  // step to land on; a stale id degrades to a fresh wizard.
  useEffect(() => {
    let mounted = true;
    void (async () => {
      try {
        const list = await fetchPublicLocations();
        if (mounted) setLocations(list);
      } catch {
        // Non-fatal: the picker shows an empty list until a retry via reload.
      }
      const id = storedSignupId();
      if (id) {
        try {
          const view = await fetchSignup(id);
          if (!mounted) return;
          setRegistration(view);
          setAdminName(view.adminName);
          setEmail(view.email);
          setClubName(view.companyName);
          setLocationId(view.locationId ?? '');
          setDescription(view.description);
          if (view.status === 'EMAIL_PENDING') {
            setPhase('code');
          } else if (view.status === 'EMAIL_VERIFIED') {
            setStep(view.companyName.length > 0 ? 2 : 1);
          } else {
            setDone(true);
          }
        } catch {
          rememberSignupId(null);
        }
      }
      if (mounted) setRestoring(false);
    })();
    return () => {
      mounted = false;
    };
  }, []);

  /** Run one wizard action with the shared busy/error/field-error handling. */
  const run = async (action: () => Promise<void>) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    setFieldErrors({});
    try {
      await action();
    } catch (e: unknown) {
      if (e instanceof SignupError) {
        setError(e.message);
        setFieldErrors(e.fields);
      } else {
        setError(e instanceof Error ? e.message : t('signup.failed'));
      }
    } finally {
      setBusy(false);
    }
  };

  const emailValid = isValidEmail(email);
  const passwordValid = password.length >= PASSWORD_MIN;

  const submitAccount = () =>
    run(async () => {
      const view = await startSignup({
        email,
        name: adminName,
        password,
        language: locale,
      });
      rememberSignupId(view.id);
      setRegistration(view);
      setCode('');
      setResent(false);
      setPhase('code');
    });

  const submitCode = () =>
    run(async () => {
      if (!registration) return;
      setRegistration(await verifySignupEmail(registration.id, code));
      setStep(1);
    });

  const resendCode = () =>
    run(async () => {
      if (!registration) return;
      setRegistration(await resendSignupCode(registration.id));
      setResent(true);
    });

  /** Abandon the staged sign-up and start over (e.g. a mistyped address). */
  const startOver = () => {
    rememberSignupId(null);
    setRegistration(null);
    setPhase('form');
    setCode('');
    setResent(false);
    setError(null);
    setFieldErrors({});
  };

  const submitClub = () =>
    run(async () => {
      if (!registration || locationId === '') return;
      setRegistration(
        await saveSignupDetails(registration.id, {
          companyName: clubName,
          locationId,
        }),
      );
      setStep(2);
    });

  const submitProfile = () =>
    run(async () => {
      if (!registration) return;
      let view = await saveSignupDescription(registration.id, description);
      if (logoBlob) {
        view = await uploadSignupLogo(registration.id, logoBlob);
        setLogoBlob(null);
      }
      setRegistration(view);
      setStep(3);
    });

  const addPhoto = (file: File) =>
    run(async () => {
      if (!registration) return;
      setRegistration(await uploadSignupPhoto(registration.id, file));
    });

  const removePhoto = (photoId: string) =>
    run(async () => {
      if (!registration) return;
      setRegistration(await deleteSignupPhoto(registration.id, photoId));
    });

  const finish = () =>
    run(async () => {
      if (!registration) return;
      setRegistration(await finalizeSignup(registration.id));
      rememberSignupId(null);
      setDone(true);
    });

  const photoCount = registration?.photos.length ?? 0;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        p: 2,
      }}
    >
      <Card sx={{ width: '100%', maxWidth: 560, border: 1, borderColor: 'divider' }}>
        <CardContent>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
            <BrandLogo height={30} />
            <Typography variant="h5" sx={{ flexGrow: 1 }}>
              {t('signup.title')}
            </Typography>
            <LanguageSelector />
          </Stack>

          {/* The route is shared as a link of its own, so a visitor can arrive
              here knowing nothing about the platform. One line on the landing
              step says what registering a club gets them and that a platform
              administrator reviews it (see `features/company-registration.md`);
              later steps already carry their own instructions. */}
          {!restoring && !done && step === 0 && phase === 'form' && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {t('signup.intro')}
            </Typography>
          )}

          {restoring ? (
            <Stack alignItems="center" sx={{ py: 6 }}>
              <CircularProgress />
            </Stack>
          ) : done && registration ? (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Alert severity="success" icon={<CheckCircleOutlineIcon />}>
                {t('signup.done.title')}
              </Alert>
              <Typography variant="body2" color="text.secondary">
                {t('signup.done.body', {
                  name: registration.companyName,
                  email: registration.email,
                })}
              </Typography>
              <Button size="large" variant="contained" href="/">
                {t('signup.done.login')}
              </Button>
            </Stack>
          ) : (
            <>
              <Stepper activeStep={step} alternativeLabel sx={{ mt: 1, mb: 2 }}>
                {STEP_KEYS.map((key) => (
                  <Step key={key}>
                    <StepLabel>{t(key)}</StepLabel>
                  </Step>
                ))}
              </Stepper>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              {step === 0 && phase === 'form' && (
                <Box
                  component="form"
                  onSubmit={(e) => {
                    e.preventDefault();
                    void submitAccount();
                  }}
                >
                  <Stack spacing={2}>
                    <Typography variant="body2" color="text.secondary">
                      {t('signup.account.intro')}
                    </Typography>
                    <TextField
                      fullWidth
                      autoFocus
                      label={t('signup.account.name')}
                      value={adminName}
                      onChange={(e) => setAdminName(e.target.value)}
                      error={Boolean(fieldErrors.name)}
                      helperText={fieldErrors.name ?? ' '}
                    />
                    <TextField
                      fullWidth
                      type="email"
                      label={t('signup.account.email')}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      error={Boolean(fieldErrors.email)}
                      helperText={fieldErrors.email ?? ' '}
                    />
                    <TextField
                      fullWidth
                      type="password"
                      label={t('signup.account.password')}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      error={
                        Boolean(fieldErrors.password) ||
                        (password.length > 0 && !passwordValid)
                      }
                      helperText={
                        fieldErrors.password ??
                        t('signup.account.passwordHelp', { min: PASSWORD_MIN })
                      }
                    />
                    <Button
                      type="submit"
                      size="large"
                      variant="contained"
                      startIcon={
                        busy ? (
                          <CircularProgress size={18} color="inherit" />
                        ) : (
                          <MarkEmailReadIcon />
                        )
                      }
                      disabled={busy || !emailValid || !passwordValid}
                    >
                      {t('signup.account.continue')}
                    </Button>
                    <Button size="small" href="/">
                      {t('signup.account.haveAccount')}
                    </Button>
                  </Stack>
                </Box>
              )}

              {step === 0 && phase === 'code' && registration && (
                <Box
                  component="form"
                  onSubmit={(e) => {
                    e.preventDefault();
                    void submitCode();
                  }}
                >
                  <Stack spacing={2}>
                    <Typography variant="body2" color="text.secondary">
                      {t('signup.code.intro', {
                        email: registration.email,
                        length: OTP_CODE_LENGTH,
                      })}
                    </Typography>
                    {resent && (
                      <Alert severity="success">{t('signup.code.resent')}</Alert>
                    )}
                    <TextField
                      fullWidth
                      autoFocus
                      label={t('signup.code.label')}
                      value={code}
                      onChange={(e) =>
                        setCode(e.target.value.replace(/\D/g, '').slice(0, OTP_CODE_LENGTH))
                      }
                      slotProps={{
                        htmlInput: { inputMode: 'numeric', maxLength: OTP_CODE_LENGTH },
                      }}
                    />
                    <Button
                      type="submit"
                      size="large"
                      variant="contained"
                      startIcon={
                        busy ? <CircularProgress size={18} color="inherit" /> : undefined
                      }
                      disabled={busy || !isOtpCodeFormat(code)}
                    >
                      {t('signup.code.verify')}
                    </Button>
                    <Stack direction="row" spacing={1} justifyContent="space-between">
                      <Button size="small" disabled={busy} onClick={() => void resendCode()}>
                        {t('signup.code.resend')}
                      </Button>
                      <Button size="small" disabled={busy} onClick={startOver}>
                        {t('signup.code.changeEmail')}
                      </Button>
                    </Stack>
                  </Stack>
                </Box>
              )}

              {step === 1 && (
                <Box
                  component="form"
                  onSubmit={(e) => {
                    e.preventDefault();
                    void submitClub();
                  }}
                >
                  <Stack spacing={2}>
                    <Typography variant="body2" color="text.secondary">
                      {t('signup.club.intro')}
                    </Typography>
                    <TextField
                      fullWidth
                      autoFocus
                      label={t('signup.club.name')}
                      value={clubName}
                      onChange={(e) => setClubName(e.target.value)}
                      error={Boolean(fieldErrors.companyName)}
                      helperText={fieldErrors.companyName ?? ' '}
                    />
                    <Autocomplete
                      fullWidth
                      // Highlight the best match so Enter picks it (MUI then
                      // swallows the key) instead of submitting the step.
                      autoHighlight
                      open={locationOpen}
                      onOpen={() => setLocationOpen(true)}
                      onClose={() => setLocationOpen(false)}
                      options={sortedLocations}
                      value={locations.find((l) => l.id === locationId) ?? null}
                      onChange={(_e, location) =>
                        setLocationId(location?.id ?? '')
                      }
                      getOptionLabel={(location) =>
                        localizedLocationLabel(location, locale)
                      }
                      isOptionEqualToValue={(a, b) => a.id === b.id}
                      filterOptions={(options, { inputValue }) =>
                        options.filter((location) =>
                          locationMatchesQuery(location, inputValue, locale),
                        )
                      }
                      noOptionsText={t('signup.club.noLocationMatches')}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          onKeyDown={(e) => {
                            // Closes the remaining hole: with the popup open
                            // but nothing highlighted (no matches), Enter would
                            // still submit the step. Selection still happens —
                            // preventDefault does not stop the event reaching
                            // Autocomplete's own handler.
                            if (e.key === 'Enter' && locationOpen) {
                              e.preventDefault();
                            }
                          }}
                          label={t('signup.club.location')}
                          placeholder={t('signup.club.locationPlaceholder')}
                          error={Boolean(fieldErrors.locationId)}
                          // A blank space keeps the step from jumping when a
                          // server-side field error arrives.
                          helperText={fieldErrors.locationId ?? ' '}
                        />
                      )}
                    />
                    <Button
                      type="submit"
                      size="large"
                      variant="contained"
                      startIcon={
                        busy ? <CircularProgress size={18} color="inherit" /> : undefined
                      }
                      disabled={busy || clubName.trim().length === 0 || locationId === ''}
                    >
                      {t('signup.continue')}
                    </Button>
                  </Stack>
                </Box>
              )}

              {step === 2 && registration && (
                <Stack spacing={2}>
                  <Typography variant="body2" color="text.secondary">
                    {t('signup.profile.intro')}
                  </Typography>
                  <CoverImagePicker
                    blob={logoBlob}
                    onChange={setLogoBlob}
                    disabled={busy}
                    label={t('signup.profile.logoLabel')}
                    caption={t('signup.profile.logoCaption')}
                    existingUrl={registration.logoUrl}
                  />
                  <TextField
                    fullWidth
                    multiline
                    minRows={4}
                    label={t('signup.profile.description')}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    error={Boolean(fieldErrors.description)}
                    helperText={fieldErrors.description ?? t('signup.profile.descriptionHelp')}
                  />
                  <Stack direction="row" spacing={1} justifyContent="space-between">
                    <Button disabled={busy} onClick={() => setStep(1)}>
                      {t('signup.back')}
                    </Button>
                    <Button
                      size="large"
                      variant="contained"
                      startIcon={
                        busy ? <CircularProgress size={18} color="inherit" /> : undefined
                      }
                      disabled={busy}
                      onClick={() => void submitProfile()}
                    >
                      {t('signup.continue')}
                    </Button>
                  </Stack>
                </Stack>
              )}

              {step === 3 && registration && (
                <Stack spacing={2}>
                  <Typography variant="body2" color="text.secondary">
                    {t('signup.photos.intro', { max: COMPANY_PHOTO_MAX })}
                  </Typography>
                  {registration.photos.length > 0 && (
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))',
                        gap: 1,
                      }}
                    >
                      {registration.photos.map((photo) => (
                        <Box key={photo.id} sx={{ position: 'relative' }}>
                          <Box
                            component="img"
                            src={photo.url}
                            alt=""
                            sx={{
                              width: '100%',
                              height: 96,
                              objectFit: 'cover',
                              borderRadius: 1,
                              display: 'block',
                            }}
                          />
                          <Tooltip title={t('signup.photos.remove')}>
                            <IconButton
                              size="small"
                              disabled={busy}
                              onClick={() => void removePhoto(photo.id)}
                              sx={{
                                position: 'absolute',
                                top: 2,
                                right: 2,
                                bgcolor: 'background.paper',
                                '&:hover': { bgcolor: 'background.paper' },
                              }}
                            >
                              <DeleteOutlineIcon fontSize="inherit" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ))}
                    </Box>
                  )}
                  <input
                    ref={photoInputRef}
                    type="file"
                    accept={ACCEPTED_TYPES.join(',')}
                    hidden
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      // Reset so re-selecting the same file still fires onChange.
                      e.target.value = '';
                      if (file) void addPhoto(file);
                    }}
                  />
                  <Button
                    startIcon={<AddPhotoAlternateIcon />}
                    disabled={busy || photoCount >= COMPANY_PHOTO_MAX}
                    onClick={() => photoInputRef.current?.click()}
                  >
                    {t('signup.photos.add')}
                  </Button>
                  <Stack direction="row" spacing={1} justifyContent="space-between">
                    <Button disabled={busy} onClick={() => setStep(2)}>
                      {t('signup.back')}
                    </Button>
                    <Button
                      size="large"
                      variant="contained"
                      startIcon={
                        busy ? (
                          <CircularProgress size={18} color="inherit" />
                        ) : (
                          <CheckCircleOutlineIcon />
                        )
                      }
                      disabled={busy}
                      onClick={() => void finish()}
                    >
                      {t('signup.photos.finish')}
                    </Button>
                  </Stack>
                </Stack>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
